import secureApi from '../../utils/secureApi';

// Feature interfaces
export interface FeatureCondition {
  name: string;
  comparator: string;
  value: any;
}

export interface Feature {
  name: string;
  scope: string;
  status: 'ENABLED' | 'DISABLED' | 'KILLED';
  clientId: string;
  conditions?: FeatureCondition[];
  createdAt?: string;
  updatedAt?: string;
}

export interface FeatureListResponse {
  paginatedResults: Feature[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// Raw API response interface
interface RawFeatureListResponse {
  features: RawFeature[];
  message?: string;
  status?: boolean;
}

interface RawFeature {
  // New backend shape (all lowercase)
  _id?: string;
  clientid?: string;
  conditions?: RawFeatureCondition[] | null;
  createdat?: string;
  updatedat?: string;
  name: string;
  scope: string;
  status: 'ENABLED' | 'DISABLED' | 'KILLED';

  // Backwards compatibility with previous shapes
  Id?: string;
  Name?: string;
  Scope?: string;
  Status?: 'ENABLED' | 'DISABLED' | 'KILLED';
  ClientId?: string;
  Conditions?: RawFeatureCondition[] | null;
  CreatedAt?: string;
  UpdatedAt?: string;
}

interface RawFeatureCondition {
  // New backend shape (all lowercase)
  name: string;
  comparator: string;
  value: any;

  // Backwards compatibility with previous shapes
  Name?: string;
  Comparator?: string;
  Value?: any;
}

// Helper to read raw feature (API may return PascalCase, camelCase, or lowercase)
function transformRawFeature(rawFeature: RawFeature | Record<string, any>): Feature {
  const r = rawFeature as Record<string, any>;
  const name = r.name ?? '';
  const scope = r.scope ?? '';
  const status = (r.status ?? 'ENABLED') as 'ENABLED' | 'DISABLED' | 'KILLED';
  const clientId = r.clientid ?? '';
  const conditions = r.conditions;
  const condList = Array.isArray(conditions)
    ? conditions.map((cond: Record<string, any>) => ({
        name: cond.name ?? '',
        comparator: cond.comparator ?? 'EQ',
        value: cond.value,
      }))
    : undefined;
  return {
    name,
    scope,
    status,
    clientId,
    conditions: condList,
    createdAt: r.createdat,
    updatedAt: r.updatedat,
  };
}

export interface CreateOrUpdateFeaturePayload {
  name: string;
  scope: string;
  status: 'ENABLED' | 'DISABLED' | 'KILLED';
  clientId: string; // Always pass when calling create-or-update
  conditions?: FeatureCondition[];
  setConditions?: boolean;
}

/**
 * List features with filters
 */
export async function listFeatures(params: {
  clientId?: string; // Optional - pass when required
  scope?: string;
  status?: 'ENABLED' | 'DISABLED' | 'KILLED';
  name?: string;
  page?: number;
  pageSize?: number;
}): Promise<FeatureListResponse> {
  const queryParams = new URLSearchParams();
  
  // Pass clientId in params when required
  if (params.clientId) {
    queryParams.append('clientId', params.clientId);
  }
  
  if (params.scope) queryParams.append('scope', params.scope);
  if (params.status) queryParams.append('status', params.status);
  if (params.name) queryParams.append('name', params.name);
  // Note: page and pageSize are optional - if not provided, API returns all features
  // Only include pagination params if explicitly provided
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString());

  const qs = queryParams.toString();
  const rawResponse = await secureApi.get<any>(
    `/api/v2/admin/feature-management/feature/list${qs ? `?${qs}` : ''}`
  );

  // Support multiple response shapes: { features }, { data: { features } }, or array
  const rawList = rawResponse?.features ?? rawResponse?.data?.features ?? (Array.isArray(rawResponse) ? rawResponse : []);
  const list = Array.isArray(rawList) ? rawList : [];

  const transformedFeatures: Feature[] = list.map(transformRawFeature);
  
  return {
    paginatedResults: transformedFeatures,
    totalCount: transformedFeatures.length,
    totalPages: 1,
    currentPage: 1,
    pageSize: transformedFeatures.length,
  };
}

/**
 * Get a single feature by name
 */
export async function getFeature(params: {
  name: string;
  clientId?: string; // Optional - pass when required
  scope: string;
}): Promise<Feature> {
  const queryParams = new URLSearchParams();
  queryParams.append('name', params.name);
  
  // Pass clientId in params when required
  if (params.clientId) {
    queryParams.append('clientId', params.clientId);
  }
  
  queryParams.append('scope', params.scope);

  const rawResponse = await secureApi.get<any>(
    `/api/v2/admin/feature-management/feature/get?${queryParams.toString()}`
  );

  // Backend returns { feature, message, status }
  const rawFeature = rawResponse?.feature ?? rawResponse;
  
  return transformRawFeature(rawFeature);
}

/**
 * Create or update a feature
 */
export async function createOrUpdateFeature(
  data: CreateOrUpdateFeaturePayload
): Promise<Feature> {
  const rawResponse = await secureApi.post<RawFeature>(
    '/api/v2/admin/feature-management/feature/create-or-update',
    data
  );
  
  return transformRawFeature(rawResponse);
}

/**
 * Delete a feature
 */
export async function deleteFeature(params: {
  name: string;
  clientId?: string; // Optional - pass when required
  scope: string;
}): Promise<void> {
  const queryParams = new URLSearchParams();
  queryParams.append('name', params.name);
  
  // Pass clientId in params when required
  if (params.clientId) {
    queryParams.append('clientId', params.clientId);
  }
  
  queryParams.append('scope', params.scope);

  await secureApi.delete(
    `/api/v2/admin/feature-management/feature/delete?${queryParams.toString()}`
  );
}
